import { Observable } from 'rxjs'
import { Container, IInit } from '../../../common/container/Container'
import { IProtocolApi } from '../api/ProtocolApi'
import {
  Evaluation,
  ImportResult,
  PatientProtocol,
  Protocol,
  ProtocolPathology,
  ProtocolStatus,
  Rule,
  RuleSet,
} from '../models/Protocol'

type Props = { apiKey: symbol }

export interface IProtocolService extends IInit {
  list(status?: ProtocolStatus): Observable<Protocol[]>
  getByID(id: string): Observable<Protocol | undefined>
  create(name: string, description: string): Observable<Protocol | undefined>
  update(id: string, name: string, description: string): Observable<Protocol | undefined>
  remove(id: string): Observable<boolean>

  submit(id: string): Observable<Protocol | undefined>
  approve(id: string): Observable<Protocol | undefined>
  reject(id: string): Observable<Protocol | undefined>
  retire(id: string): Observable<Protocol | undefined>
  newVersion(id: string): Observable<Protocol | undefined>

  createRuleSet(rs: Partial<RuleSet>): Observable<RuleSet | undefined>
  removeRuleSet(id: string): Observable<boolean>
  createRule(r: Partial<Rule>): Observable<Rule | undefined>
  updateRule(id: string, r: Partial<Rule>): Observable<Rule | undefined>
  removeRule(id: string): Observable<boolean>

  assignmentsByPatient(patientID: string): Observable<PatientProtocol[]>
  pathologies(protocolID: string): Observable<ProtocolPathology[]>
  setPathologies(protocolID: string, pathologyIDs: string[]): Observable<boolean>

  evaluations(patientID: string, limit?: number): Observable<Evaluation[]>
  evaluateNow(patientID: string): Observable<Evaluation[]>

  importExcel(file: File, name: string): Observable<ImportResult | undefined>
}

export class ProtocolService implements IProtocolService {
  private _container!: Container
  private _api!: IProtocolApi
  private readonly _apiKey: symbol

  constructor(p: Props) {
    this._apiKey = p.apiKey
  }

  init(c: Container) {
    this._container = c
    this._api = this._container.get<IProtocolApi>(this._apiKey)
  }

  list(status?: ProtocolStatus) {
    return this._api.list(status)
  }
  getByID(id: string) {
    return this._api.getByID(id)
  }
  create(name: string, description: string) {
    return this._api.create(name, description)
  }
  update(id: string, name: string, description: string) {
    return this._api.update(id, name, description)
  }
  remove(id: string) {
    return this._api.remove(id)
  }

  submit(id: string) {
    return this._api.transition(id, 'submit')
  }
  approve(id: string) {
    return this._api.transition(id, 'approve')
  }
  reject(id: string) {
    return this._api.transition(id, 'reject')
  }
  retire(id: string) {
    return this._api.transition(id, 'retire')
  }
  newVersion(id: string) {
    return this._api.transition(id, 'versions')
  }

  createRuleSet(rs: Partial<RuleSet>) {
    return this._api.createRuleSet(rs)
  }
  removeRuleSet(id: string) {
    return this._api.removeRuleSet(id)
  }
  createRule(r: Partial<Rule>) {
    return this._api.createRule(r)
  }
  updateRule(id: string, r: Partial<Rule>) {
    return this._api.updateRule(id, r)
  }
  removeRule(id: string) {
    return this._api.removeRule(id)
  }

  assignmentsByPatient(patientID: string) {
    return this._api.assignmentsByPatient(patientID)
  }
  pathologies(protocolID: string) {
    return this._api.pathologies(protocolID)
  }
  setPathologies(protocolID: string, pathologyIDs: string[]) {
    return this._api.setPathologies(protocolID, pathologyIDs)
  }

  evaluations(patientID: string, limit?: number) {
    return this._api.evaluations(patientID, limit)
  }
  evaluateNow(patientID: string) {
    return this._api.evaluateNow(patientID)
  }

  importExcel(file: File, name: string) {
    return this._api.importExcel(file, name)
  }
}
